import * as React from 'react'
import { ReactElement, useContext, useEffect, useState } from 'react'
import BaseCombobox, { SubComboboxProps } from '../../../components/Combobox/BaseCombobox'
import { formatFolderName } from '../../../helpers/documentHelper'
import { fetchFolderDriveItemTreeBatched, FolderDriveItem } from '../../../components/Document/DocumentFlatTree'
import useAxios from '../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import { appInsights } from '../../../pages/App'
import AuthorizationContext from '../../../context/AuthorizationContext'

export enum DocumentSelectionType {
    // noinspection JSUnusedGlobalSymbols
    FolderOnly = 'FOLDER_ONLY',
    DocumentOnly = 'DOCUMENT_ONLY',
    Both = 'BOTH',
}

export type DocumentComboboxProps = SubComboboxProps<FolderDriveItem> & {
    selectionType?: DocumentSelectionType
    documentRootId?: string
    // Dossier reference (e.g. "TST0710") forwarded to the FetchFolderTree telemetry event
    // so request counts can be correlated to a specific dossier in App Insights.
    referenceNumber?: string
    onLazyLoaded?: (items: FolderDriveItem[]) => void
    // Notifies the parent when the folder-tree fetch fails (missing documentRootId, 4xx, …).
    // The parent renders the user-facing message in the dialog body so the combobox stays
    // in its form-field slot. Called with undefined when the error clears.
    onError?: (message: string | undefined) => void
}

export const getEntityIds = (children: FolderDriveItem[]): string[] => {
    return children.flatMap(child => [child.id, ...getEntityIds(child.children || [])])
}

export default function DocumentCombobox(props: DocumentComboboxProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const { host } = useContext(AuthorizationContext)

    const [lazyEntities, setLazyEntities] = useState<FolderDriveItem[]>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const onError = props.onError
    useEffect(() => {
        onError?.(undefined)
        if (props.entities) {
            setLazyEntities(props.entities)
            setIsLoading(false)
            return
        }
        // No pre-loaded entities AND no rootId means the dossier has no document location
        // wired up. Skip the Graph call (it would 400) and surface a meaningful message via
        // the parent dialog. Still emit a FetchFolderTree event with success:false so App
        // Insights surfaces dossiers without a documentRootId for follow-up.
        if (!props.documentRootId) {
            setLazyEntities([])
            setIsLoading(false)
            onError?.(translate('file.pivot.document.error.not_found'))
            appInsights.trackEvent({
                name: 'DocumentCombobox.FetchFolderTree',
                properties: {
                    referenceNumber: props.referenceNumber,
                    rootId: undefined,
                    foldersOnly: props.selectionType === DocumentSelectionType.FolderOnly,
                    success: false,
                    errorStatus: 'no_root_id',
                },
            })
            return
        }
        setIsLoading(true)
        fetchFolderDriveItemTreeBatched(
            axios,
            translate,
            host?.DriveId,
            props.documentRootId,
            props.selectionType === DocumentSelectionType.FolderOnly,
            props.referenceNumber,
        )
            .then(response => {
                setLazyEntities(response)
                props.onLazyLoaded?.(response)
            })
            .catch(error => {
                setLazyEntities([])
                // fetchFolderDriveItemTree rejects with a translated string for 403/404; for
                // other failures we fall back to a generic not-found message.
                onError?.(typeof error === 'string' ? error : translate('file.pivot.document.error.not_found'))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [props.entities, props.documentRootId])

    const rootEntities = React.useMemo(() => {
        const childIds = getEntityIds(lazyEntities ?? [])
        return lazyEntities?.filter(entity => !childIds.includes(entity.parent?.id)) ?? []
    }, [lazyEntities])

    return (
        <BaseCombobox<FolderDriveItem>
            filter={entity => {
                switch (props.selectionType ?? DocumentSelectionType.Both) {
                    case DocumentSelectionType.FolderOnly:
                        return entity.itemType === 'branch'
                    case DocumentSelectionType.DocumentOnly:
                        return entity.itemType === 'leaf'
                }
                return true
            }}
            isGroup={entity => entity.itemType === 'branch'}
            isSelectableGroup={entity =>
                entity.itemType === 'branch' && (props.selectionType ?? DocumentSelectionType.Both) !== DocumentSelectionType.DocumentOnly
            }
            getGroupLabel={entity => (entity.itemType === 'branch' ? formatFolderName(entity.name) : entity.name)}
            getGroupValue={entity => entity.id}
            getGroupEntities={entity => entity.children}
            getEntityLabel={entity => (entity.itemType === 'branch' ? formatFolderName(entity.name) : entity.name)}
            getEntityValue={entity => entity.id}
            placeholderKey={`base.documentcombobox.${props.selectionType.toLowerCase()}.placeholder`}
            {...props}
            entities={rootEntities}
            // While the folder structure is loading, show "Bezig met laden..." instead of the
            // normal "Selecteer map" placeholder so users don't mistake the empty dropdown
            // for "no folders found". Overrides any caller-supplied placeholder keys.
            {...(isLoading && { placeholderPrefixKey: '', placeholderKey: 'base.loading', disabled: true })}
        />
    )
}
